import React, { useState, useEffect, useContext } from 'react';
import './ModuleManagement.css';
import ModuleRow from '../ModuleRow/ModuleRow';
import InteractiveList from '../InteractiveList/InteractiveList';
import ModuleDetailModal from '../ModuleDetailModal/ModuleDetailModal';
import ListUpdateMessage from '../ListUpdateMessage/ListUpdateMessage';
import convenienceApi from '../../common/api/convenience';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import { AdminContext } from '../../contexts/AdminContext';
import { ModuleAdminProvider } from '../../contexts/ModuleAdminContext';
import logComponent from '../../common/logComponent';

export default function ModuleManagement() {
  const environmentContext = useContext(EnvironmentContext);
  const adminContext = useContext(AdminContext);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [modulesError, setModulesError] = useState(false);
  const [modules, setModules] = useState([]);
  const [group, setGroup] = useState(null);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState(false);
  const [visibleModule, setVisibleModule] = useState(null);
  const [updatedModule, setUpdatedModule] = useState(null);
  const [mostRecentModuleUpdate, setMostRecentModuleUpdate] = useState(null);
  const { activeUser: user, hasToken, organization } = environmentContext;
  const { modules: adminContextModules, groups } = adminContext;

  if (logComponent('ModuleManagement')) { console.debug('ModuleManagement render'); }

  useEffect(() => {
    if (logComponent('ModuleManagement')) { console.debug('ModuleManagement onMount'); }
    if (!hasToken) return;

  }, [])


  useEffect(() => {
    if (logComponent('ModuleManagement')) { console.debug('ModuleManagement useEffect [environmentContext.hasToken]'); }
    if (!hasToken) return;

    loadModules();
    loadGroups();
  }, [environmentContext.hasToken])


  const loadGroups = () => {
    if (logComponent('ModuleManagement')) { console.debug('ModuleManagement loadGroups'); }
    if (!hasToken) return;

    if (adminContext.isFresh('groups')) return;

    setGroupsLoading(true);
    setGroupsError(false);

    convenienceApi.getGroupsByAdmin(user)
      .then(result => {
        adminContext.setGroups(result);
        setGroupsLoading(false);
        setGroupsError(false);
      })
      .catch(error => {
        setGroupsLoading(false);
        setGroupsError(true);
      })
  }  

  const loadModules = () => {
    if (logComponent('ModuleManagement')) { console.debug('ModuleManagement loadGroups'); }
    if (!hasToken) return;

    if (adminContext.isFresh('modules')) return;
    setModulesLoading(true);
    setModulesError(false);

    convenienceApi.getModulesByAdmin(user, organization)
      .then(
        result => {
          adminContext.setModules(result);
          setModulesLoading(false);
          setModulesError(false);
        },
        error => {
          setModulesLoading(false);
          setModulesError(true);
        });
  }

  const hideVisibleModule = () => {
    if (logComponent('ModuleManagement')) { console.debug('ModuleManagement hideVisibleModule'); }
    setVisibleModule(null);
  }

  const toggleVisibleModule = (moduleToToggle) => {
    if (logComponent('ModuleManagement')) { console.debug('ModuleManagement moduleToToggle'); }
    let newVisibleModule = null;

    if (visibleModule !== moduleToToggle) {
      newVisibleModule = moduleToToggle;
    }

    setVisibleModule(newVisibleModule);
  }

  const setUpdatedModuleThis = (updatedModuleThis) => {
    if (logComponent('ModuleManagement')) { console.debug('ModuleManagement setUpdatedModuleThis'); }
    const updatedModules = adminContext.modules.map(module => module.moduleId !== updatedModuleThis.moduleId ? module : updatedModuleThis);
    adminContext.setModules(updatedModules);

    setUpdatedModule(updatedModuleThis);
    setMostRecentModuleUpdate(Date.now());

  }

  const getColumns = () => {
    if (logComponent('ModuleManagement')) { console.debug('ModuleManagement getColumns'); }

    if (user.groupTypeString === 'Status Solutions') { //here
      return [  
        { name: 'Module', sortField: 'Name' },
        { name: '', style: { width: '64px'}}
      ];
    } else if (user.groupTypeString === 'Facility' || user.groupTypeString === 'Corporate') {
      // For facility admins
        let columns = [
            { name: 'Module', sortField: 'Name' },
            { name: 'Status', style: { width: '18rem' }, hideAt: 'sm' },
            { name: '', style: { width: '64px' } }
        ];
      return columns;      
    } else {
      return [];
    }
  }

  const getGroupTypeFilterOptions = () => {
    if (logComponent('ModuleManagement')) { console.debug('ModuleManagement getGroupTypeFilterOptions'); }

    /* eslint-disable */
    switch(user.groupTypeString) {
      case 'Status Solutions':
        return [
            {
              text: 'Corporate',
              criteria: [{
                field: 'IsCorporate',
                acceptedValues: [true]
              }]
            }, {
              text: 'Facility',
              criteria: [{
                field: 'IsFacility',
                acceptedValues: [true]
              }]
            }, {
              text: 'Family',
              criteria: [{
                field: 'IsFamily',
                acceptedValues: [true]
              }]
            }, {
              text: 'Status Solutions',
              criteria: [{
                field: 'IsStatus',
                acceptedValues: [true]
              }]
            }
          ];
          break;
      case 'Facility': 
        return [{
          text: 'Facility',
          criteria: [{
            field: 'IsFacility',
            acceptedValues: [true]
          }]
        }, {
          text: 'Family',
          criteria: [{
            field: 'IsFamily',
            acceptedValues: [true]
          }]
        }]
        break;
      default:
        break;
    }
    /* eslint-enable */
  }

  const getStatusFilterFieldName = () => {
    if (logComponent('ModuleManagement')) { console.debug('ModuleManagement getStatusFilterFieldName'); }

    return user.groupTypeString === 'Corporate' ? 'CorporationStatus' : user.groupTypeString === 'Facility' ? 'FacilityStatus' : '';
  }

  return (
    <div className="Module-Management">
      <ModuleAdminProvider>

      {
        updatedModule &&
        <div className="Module-Management__update-message-wrapper">
          <ListUpdateMessage
            message={`${updatedModule.name} module has been successfully updated.`}
            lastUpdate={mostRecentModuleUpdate} />
        </div>
      }
      <h2 className="Module-Management__heading">Modules</h2>
      <InteractiveList
        columns={getColumns()}
        singularItemTerm="module"
        pluralItemTerm="modules"
        searchTextFields={["name", "description"]}
        searchPlaceholder="Search modules"
        defaultSortField="Name"
        filters={[
          {
            title: 'Dependency',
            type: 'checkbox',
            name: 'dependency',
            options: [
              {
                text: 'SARA',
                criteria: [{
                  field: 'SaraDependent',
                  acceptedValues: [true]
                }]
              },
              {
                text: 'CATIE',
                criteria: [{
                  field: 'CatieDependent',
                  acceptedValues: [true]
                }]
              }, {
                text: 'eMessenger',
                criteria: [{
                  field: 'EMessengerDependent',
                  acceptedValues: [true]
                }]
              }
            ]
          },
          {
            title: 'User type',
            type: 'checkbox',
            name: 'groupType',
            hide: user.groupTypeString === 'Corporate',
            options: getGroupTypeFilterOptions()
          },
          {
            title: 'Status',
            type: 'checkbox',
            name: 'status',
            hide: user.groupTypeString === 'Status Solutions',
            options: [{
              text: ' Disabled',
              criteria: [{
                field: getStatusFilterFieldName(),
                acceptedValues: ['Disabled']
              }]
            }, {
              text: 'Enabled',
              criteria: [{
                field: getStatusFilterFieldName(),
                acceptedValues: ['Enabled']
              }]
            }, {
              text: 'Not Available',
              criteria: [{
                field: getStatusFilterFieldName(),
                acceptedValues: ['Not Available']
              }]
            }],

          }
        ]}
        items={adminContextModules}
        itemsLoading={modulesLoading}
        rowComponent={ModuleRow}
        showDetail={toggleVisibleModule}
        keyField="moduleId"
        reloadAction={loadModules} />
      {
        visibleModule &&
        <ModuleDetailModal
          groups={groups}
          groupsLoading={groupsLoading}
          groupsError={groupsError}
          visibleModule={visibleModule}
          hide={hideVisibleModule}
          changeModule={toggleVisibleModule}
          onUpdate={setUpdatedModuleThis} />
      }
      </ModuleAdminProvider>
    </div>

  );
}